import { Alert, AlertTitle, Divider, LinearProgress, Tab, Tabs, Tooltip } from "@mui/material";
import { useEffect, useState } from "react";
import { Link, Route, Routes, useLocation, useNavigate } from "react-router";
import { Connections } from "../../modules/employees/components/connections/connections";
import { CsvUpload } from "../../modules/employees/components/csv-upload";
import { EmployeeDataGridWrapper } from "../../modules/employees/components/employee-data-grid-wrapper";
import { HRISIntegrationCard } from "../../modules/employees/components/hrisintegration-card";
import { OptOuts } from "../../modules/employees/components/opt-outs/opt-outs";
import { OptimizedEmployeeDataGridWrapper } from "../../modules/employees/components/optimized-employee-data-grid-wrapper";
import { Permissions } from "../../modules/employees/components/permissions/permissions";
import { useEmployeeData } from "../../modules/employees/hooks/use-employee-data";
import { SupabaseAccountEmployeeSyncConfigService } from "../../modules/generic/server/supabase-account-service";
import { InsightKeyFieldsTab } from "../../modules/insights/components/insight-key-fields-tab";
import { Page } from "../../modules/layout/components/page";
import { PageContent } from "../../modules/layout/components/page-content";
import { PageHeader } from "../../modules/layout/components/page-header";
import { PageHeading } from "../../modules/layout/components/page-heading";
import { usePermissions } from "../../server/auth/use-permissions";
import { useFlag } from "../../server/optimizely";
import { useSupabase, useSupabaseCallback } from "../../server/supabase/hooks";

import type { Database } from "../../__definitions__/database-definitions";
import type { FC } from "react";

const DefaultPage: FC<{ syncType?: string | null }> = ({ syncType }) => {
  const navigate = useNavigate();
  useEffect(() => {
    if (syncType === undefined) return;
    void navigate(`/manage/employees/${(syncType || "HRIS").toLocaleLowerCase()}`);
  });
  return null;
};

const EmployeeGridRoute: FC<{
  type: "hris" | "csv";
  optimized: boolean;
  flagLoaded: boolean;
  legacyLoading: boolean;
  legacyData: ReturnType<typeof useEmployeeData>["data"];
  reloadCount: number;
}> = ({ type, optimized, flagLoaded, legacyLoading, legacyData, reloadCount }) => {
  if (!flagLoaded || (!optimized && legacyLoading)) return <LinearProgress />;
  if (optimized) return <OptimizedEmployeeDataGridWrapper type={type} reloadCount={reloadCount} />;
  return (
    <EmployeeDataGridWrapper employeeData={legacyData ?? { data: [], keys: [] }} type={type} />
  );
};

const ManageEmployeesPage: FC = () => {
  const location = useLocation();
  const { super_admin } = usePermissions();
  const [optOutsEnabled] = useFlag("opt_outs");
  const [employeeDataSpeedupEnabled, , employeeDataSpeedupFlagLoaded] =
    useFlag("employee_data_speedup");
  const [syncType, setSyncType] =
    useState<Database["public"]["Tables"]["account_employee_sync_config"]["Row"]["sync_type"]>();
  const [error, setError] = useState<boolean>(false);

  useSupabase(async ({ supabase }) => {
    const { data: accData, error: accError } = await new SupabaseAccountEmployeeSyncConfigService(
      supabase,
    ).maybeGet();
    if (accError) return setError(true);
    setSyncType(accData?.sync_type || null);
  }, []);

  const changeSyncType = useSupabaseCallback(
    async (
      { supabase, account_id },
      sync_type: Database["public"]["Tables"]["account_employee_sync_config"]["Row"]["sync_type"],
    ) => {
      setSyncType(sync_type);
      await new SupabaseAccountEmployeeSyncConfigService(supabase).upsert(
        {
          account_id,
          sync_type,
        },
        { onConflict: "account_id" },
      );
    },
    [],
  );

  const {
    data: employees,
    loading: employeeDataLoading,
    reload: reloadLegacyEmployeeData,
  } = useEmployeeData(employeeDataSpeedupFlagLoaded && !employeeDataSpeedupEnabled);
  const [employeeDataReloadCount, setEmployeeDataReloadCount] = useState(0);

  const reloadEmployeeData = (): void => {
    if (employeeDataSpeedupEnabled) setEmployeeDataReloadCount((count) => count + 1);
    else reloadLegacyEmployeeData();
  };

  return (
    <Page title="Manage Employees | ChangeEngine">
      <PageHeader display="flex">
        <PageHeading
          heading="Manage Your Employees"
          subheading="Upload and update from your employee database"
        />
      </PageHeader>
      <PageContent>
        <>
          {error && (
            <Alert severity="error">
              <AlertTitle>Something went wrong</AlertTitle>
            </Alert>
          )}
          <Tabs
            indicatorColor="primary"
            scrollButtons="auto"
            textColor="primary"
            value={location.pathname.split("/").slice(-1)[0]!.toUpperCase()}
            variant="scrollable"
            sx={{ mt: 3 }}
          >
            {["HRIS", "CSV"].map((method) => {
              const disabled = ![method, null, undefined].includes(syncType);
              return (
                <Tab
                  key={method}
                  style={{ pointerEvents: "auto" }}
                  label={
                    !disabled ? (
                      method
                    ) : (
                      <Tooltip
                        title={`${
                          method === "HRIS" ? "CSV" : "HRIS"
                        } employee management is configured, to modify employee sync method please contact support`}
                      >
                        <span>{method}</span>
                      </Tooltip>
                    )
                  }
                  value={method}
                  disabled={disabled}
                  sx={{ ml: 2 }}
                  component={Link}
                  to={disabled ? "#" : `/manage/employees/${method.toLocaleLowerCase()}`}
                />
              );
            })}
            {super_admin && (
              <Tab
                style={{ pointerEvents: "auto" }}
                label="Permissions"
                value="PERMISSIONS"
                sx={{ ml: 2 }}
                component={Link}
                to="/manage/employees/permissions"
              />
            )}
            <Tab
              style={{ pointerEvents: "auto" }}
              label="Connections"
              value="CONNECTIONS"
              sx={{ ml: 2 }}
              component={Link}
              to="/manage/employees/connections"
            />
            <Tab
              style={{ pointerEvents: "auto" }}
              label="Insights Key Fields"
              value="INSIGHTS_KEY_FIELDS"
              sx={{ ml: 2 }}
              component={Link}
              to="/manage/employees/insights_key_fields"
            />
            {optOutsEnabled && (
              <Tab
                style={{ pointerEvents: "auto" }}
                label="Opt-Outs and Blocks"
                value="OPT_OUTS"
                sx={{ ml: 2 }}
                component={Link}
                to="/manage/employees/opt_outs"
              />
            )}
          </Tabs>
          <Divider sx={{ mb: 3 }} />
          <Routes>
            <Route path="/" element={<DefaultPage syncType={syncType} />} />
            <Route
              path="hris"
              element={
                <HRISIntegrationCard
                  onConnect={async () => {
                    await changeSyncType("HRIS");
                  }}
                />
              }
            />
            <Route
              path="csv"
              element={
                <CsvUpload
                  onUpload={async () => {
                    await changeSyncType("CSV");
                  }}
                  onComplete={reloadEmployeeData}
                />
              }
            />
            <Route path="permissions" element={<Permissions />} />
            <Route path="connections" element={<Connections />} />
            <Route path="insights_key_fields" element={<InsightKeyFieldsTab />} />
            {optOutsEnabled && <Route path="opt_outs" element={<OptOuts />} />}
          </Routes>
          <Divider sx={{ mt: 3, mb: 3 }} />
          <Routes>
            <Route
              path="hris"
              element={
                <EmployeeGridRoute
                  type="hris"
                  optimized={employeeDataSpeedupEnabled}
                  flagLoaded={employeeDataSpeedupFlagLoaded}
                  legacyLoading={employeeDataLoading}
                  legacyData={employees}
                  reloadCount={employeeDataReloadCount}
                />
              }
            />
            <Route
              path="csv"
              element={
                <EmployeeGridRoute
                  type="csv"
                  optimized={employeeDataSpeedupEnabled}
                  flagLoaded={employeeDataSpeedupFlagLoaded}
                  legacyLoading={employeeDataLoading}
                  legacyData={employees}
                  reloadCount={employeeDataReloadCount}
                />
              }
            />
          </Routes>
        </>
      </PageContent>
    </Page>
  );
};

export default ManageEmployeesPage;
